import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, downloadFile, errorText, formatDate, formatMoney, formatQty } from '../api';
import { useAuth } from '../auth';
import { verfolgungsLink, versenderName } from '../versender';
import { Badge, Card, CheckField, Empty, Loading, Notice, TextField } from '../components/ui';
import type {
  Gefahrgut,
  Article,
  OutboundLine,
  OutboundOrder,
  OutboundStatus,
  Partner,
  PickTask,
  PicklistResult,
  Warehouse,
} from '../types';

const STATUS_STYLE: Record<OutboundStatus, 'warn' | 'info' | 'ok' | 'default'> = {
  offen: 'warn',
  kommissionierung: 'info',
  kommissioniert: 'info',
  versendet: 'ok',
  storniert: 'default',
};

const STATUS_LABEL: Record<OutboundStatus, string> = {
  offen: 'offen',
  kommissionierung: 'in Kommissionierung',
  kommissioniert: 'kommissioniert',
  versendet: 'versendet',
  storniert: 'storniert',
};

interface DraftLine {
  article: Article;
  qtyOrdered: string;
}

export function OutboundPage() {
  const { owners, ownerId: globalOwner, can } = useAuth();
  const mayCreate = can('admin', 'kaufmann', 'disponent');
  const mayBook = can('admin', 'schichtleiter', 'lagerist', 'disponent');

  const [sp] = useSearchParams();
  const [orders, setOrders] = useState<OutboundOrder[] | null>(null);
  const [openOnly, setOpenOnly] = useState(true);
  const [openId, setOpenId] = useState<string | null>(sp.get('open'));
  // Auftrag aus der Kopfzeilen-Suche direkt öffnen.
  useEffect(() => {
    const id = sp.get('open');
    if (id) setOpenId(id);
  }, [sp]);
  const [detail, setDetail] = useState<
    { order: OutboundOrder; lines: OutboundLine[]; tasks: PickTask[] } | null
  >(null);
  const [gefahrgut, setGefahrgut] = useState<Gefahrgut | null>(null);
  const [detailTab, setDetailTab] = useState<'positionen' | 'kommi' | 'beleg' | 'sendung'>(
    'positionen',
  );
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [customers, setCustomers] = useState<Partner[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState({
    ownerId: globalOwner,
    customerId: '',
    warehouseId: '',
    reference: '',
    requestedAt: '',
    priority: '5',
    note: '',
    kind: 'auftrag' as 'auftrag' | 'angebot',
    validUntil: '',
  });
  const [draftLines, setDraftLines] = useState<DraftLine[]>([]);
  const [term, setTerm] = useState('');
  const [hits, setHits] = useState<Article[]>([]);

  const load = useCallback(async () => {
    setError('');
    try {
      setOrders(
        await api<OutboundOrder[]>('/api/outbound/orders', {
          query: { openOnly: openOnly ? 'true' : undefined },
        }),
      );
    } catch (err) {
      setError(errorText(err));
    }
  }, [openOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    api<Warehouse[]>('/api/warehouse').then(setWarehouses).catch(() => setWarehouses([]));
    api<Partner[]>('/api/partners', { query: { role: 'customer' } })
      .then(setCustomers)
      .catch(() => setCustomers([]));
  }, []);

  useEffect(() => {
    if (!term.trim() || !draft.ownerId) {
      setHits([]);
      return;
    }
    const timer = setTimeout(() => {
      api<Article[]>('/api/articles/lookup', { query: { q: term.trim() } })
        .then((found) => setHits(found.filter((a) => a.owner_id === draft.ownerId)))
        .catch(() => setHits([]));
    }, 200);
    return () => clearTimeout(timer);
  }, [term, draft.ownerId]);

  const loadDetail = useCallback(async (id: string) => {
    setDetail(null);
    setGefahrgut(null);
    try {
      const [daten, adr] = await Promise.all([
        api<{ order: OutboundOrder; lines: OutboundLine[]; tasks: PickTask[] }>(
          `/api/outbound/orders/${id}`,
        ),
        api<Gefahrgut>(`/api/outbound/orders/${id}/gefahrgut`),
      ]);
      setDetail(daten);
      setGefahrgut(adr);
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    if (openId) {
      setDetailTab('positionen');
      void loadDetail(openId);
    }
  }, [openId, loadDetail]);

  async function ladeLieferschein() {
    if (!detail) return;
    try {
      await downloadFile(`/api/printing/delivery-note/${detail.order.id}`);
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function angebotAnnehmen() {
    if (!openId) return;
    setBusy(true);
    setError('');
    try {
      const auftrag = await api<{ number: string }>(`/api/sales/quotes/${openId}/accept`, {
        method: 'POST',
      });
      setMessage(`Angebot angenommen — Auftrag ${auftrag.number} angelegt.`);
      setOpenId(null);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function createOrder() {
    if (draftLines.length === 0) return setError('Mindestens eine Position wird gebraucht.');
    setBusy(true);
    setError('');
    try {
      const created = await api<OutboundOrder>('/api/outbound/orders', {
        method: 'POST',
        body: {
          ownerId: draft.ownerId,
          customerId: draft.customerId || null,
          warehouseId: draft.warehouseId,
          reference: draft.reference || null,
          requestedAt: draft.requestedAt || null,
          priority: Number(draft.priority) || 5,
          note: draft.note || null,
          kind: draft.kind,
          validUntil: draft.kind === 'angebot' ? draft.validUntil || null : null,
          lines: draftLines.map((line) => ({
            articleId: line.article.id,
            qtyOrdered: Number(line.qtyOrdered.replace(',', '.')),
          })),
        },
      });
      setMessage(`Auftrag ${created.number} angelegt.`);
      setShowForm(false);
      setDraftLines([]);
      await load();
      setOpenId(created.id);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function makePicklist() {
    if (!detail) return;
    setBusy(true);
    setError('');
    try {
      const result = await api<PicklistResult>(`/api/outbound/orders/${detail.order.id}/picklist`, {
        method: 'POST',
      });
      setMessage(
        result.shortfalls.length === 0
          ? `Pickliste mit ${result.tasks} Aufträgen erzeugt.`
          : `Pickliste mit ${result.tasks} Aufträgen erzeugt. Unterdeckung: ` +
            result.shortfalls.map((s) => `${s.sku} fehlen ${s.missing}`).join(', '),
      );
      await loadDetail(detail.order.id);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function ship() {
    if (!detail) return;
    setBusy(true);
    setError('');
    try {
      const result = await api<{ qtyShipped: number }>(
        `/api/outbound/orders/${detail.order.id}/ship`,
        { method: 'POST' },
      );
      setMessage(`${formatQty(result.qtyShipped)} Einheiten versendet.`);
      await loadDetail(detail.order.id);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    if (!detail) return;
    setBusy(true);
    try {
      await api(`/api/outbound/orders/${detail.order.id}/cancel`, { method: 'POST' });
      setMessage('Auftrag storniert.');
      await loadDetail(detail.order.id);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <div className="toolbar">
        <CheckField label="Nur offene Aufträge" checked={openOnly} onChange={setOpenOnly} />
        <span className="spacer" />
        {mayCreate && (
          <button className="primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Abbrechen' : 'Neuer Auftrag'}
          </button>
        )}
      </div>

      {showForm && (
        <Card title={draft.kind === 'angebot' ? 'Neues Angebot' : 'Neuer Auftrag'}>
          <div className="form-grid">
            <label className="field">
              <span>Art des Belegs</span>
              <select
                value={draft.kind}
                onChange={(e) =>
                  setDraft({ ...draft, kind: e.target.value as 'auftrag' | 'angebot' })
                }
              >
                <option value="auftrag">Auftrag — wird kommissioniert und versendet</option>
                <option value="angebot">Angebot — reserviert keine Ware</option>
              </select>
            </label>
            {draft.kind === 'angebot' && (
              <label className="field">
                <span>Bindefrist</span>
                <input
                  type="date"
                  value={draft.validUntil}
                  onChange={(e) => setDraft({ ...draft, validUntil: e.target.value })}
                />
                <span className="small muted">Nach Ablauf lässt es sich nicht mehr annehmen</span>
              </label>
            )}
            <label className="field">
              <span>Eigentümer der Ware</span>
              <select
                value={draft.ownerId}
                onChange={(e) => setDraft({ ...draft, ownerId: e.target.value })}
              >
                <option value="">— bitte wählen —</option>
                {owners.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.is_self ? `${owner.name} · eigene Ware` : owner.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Lager</span>
              <select
                value={draft.warehouseId}
                onChange={(e) => setDraft({ ...draft, warehouseId: e.target.value })}
              >
                <option value="">— bitte wählen —</option>
                {warehouses.map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.code} · {warehouse.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Kunde (optional)</span>
              <select
                value={draft.customerId}
                onChange={(e) => setDraft({ ...draft, customerId: e.target.value })}
              >
                <option value="">— keiner —</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </label>
            <TextField
              label="Referenz"
              value={draft.reference}
              onChange={(v) => setDraft({ ...draft, reference: v })}
              hint="Bestellnummer des Kunden"
            />
            <TextField
              label="Liefertermin"
              type="date"
              value={draft.requestedAt}
              onChange={(v) => setDraft({ ...draft, requestedAt: v })}
            />
            <label className="field">
              <span>Dringlichkeit</span>
              <select
                value={draft.priority}
                onChange={(e) => setDraft({ ...draft, priority: e.target.value })}
              >
                <option value="1">1 — Express</option>
                <option value="3">3 — hoch</option>
                <option value="5">5 — normal</option>
                <option value="7">7 — niedrig</option>
              </select>
              <span className="small muted">steuert die Reihenfolge im Lager</span>
            </label>
            <TextField
              label="Bemerkung"
              value={draft.note}
              onChange={(v) => setDraft({ ...draft, note: v })}
            />
          </div>

          {draft.ownerId ? (
            <>
              <label className="field">
                <span>Artikel suchen</span>
                <input
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder="Artikelnummer, EAN oder Bezeichnung"
                />
              </label>
              {hits.length > 0 && (
                <div className="pick-list">
                  {hits.map((hit) => (
                    <button
                      key={hit.id}
                      onClick={() => {
                        setDraftLines((current) => [...current, { article: hit, qtyOrdered: '' }]);
                        setTerm('');
                        setHits([]);
                      }}
                    >
                      <strong>{hit.name}</strong>
                      <div className="small muted mono">
                        {hit.sku} · Bestand {formatQty(hit.stock_total, hit.unit)}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <Notice kind="info">Zuerst den Eigentümer wählen.</Notice>
          )}

          {draftLines.length > 0 && (
            <table style={{ marginTop: 10 }}>
              <thead>
                <tr>
                  <th>Pos</th>
                  <th>Artikel</th>
                  <th className="num">Bestand</th>
                  <th className="num">Menge</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {draftLines.map((line, index) => (
                  <tr key={`${line.article.id}-${index}`}>
                    <td>{index + 1}</td>
                    <td>
                      <span className="mono">{line.article.sku}</span> {line.article.name}
                    </td>
                    <td className="num muted">{formatQty(line.article.stock_total)}</td>
                    <td className="num" style={{ width: 130 }}>
                      <input
                        style={{ textAlign: 'right' }}
                        value={line.qtyOrdered}
                        onChange={(e) =>
                          setDraftLines((current) =>
                            current.map((entry, i) =>
                              i === index ? { ...entry, qtyOrdered: e.target.value } : entry,
                            ),
                          )
                        }
                      />
                    </td>
                    <td className="num">
                      <button
                        className="small ghost"
                        onClick={() => setDraftLines((c) => c.filter((_, i) => i !== index))}
                      >
                        entfernen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="primary" onClick={createOrder} disabled={busy}>
              {busy ? 'Anlegen …' : draft.kind === 'angebot' ? 'Angebot anlegen' : 'Auftrag anlegen'}
            </button>
            <button onClick={() => setShowForm(false)}>Abbrechen</button>
          </div>
        </Card>
      )}

      <Card title="Aufträge" padded={false}>
        {!orders ? (
          <Loading />
        ) : orders.length === 0 ? (
          <Empty>
            {openOnly ? (
              <>
                Keine offenen Aufträge.{' '}
                <button className="ghost small" onClick={() => setOpenOnly(false)}>
                  Auch abgeschlossene zeigen
                </button>
              </>
            ) : (
              'Keine Aufträge vorhanden.'
            )}
          </Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Nummer</th>
                <th>Referenz</th>
                <th>Kunde</th>
                <th>Eigentümer</th>
                <th>Termin</th>
                <th>Dringlichkeit</th>
                <th>Status</th>
                <th className="num">Gepickt</th>
                <th className="num">Offene Picks</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td className="mono">
                    {order.number}
                    {order.kind === 'angebot' && (
                      <>
                        {' '}
                        <Badge kind="info">Angebot</Badge>
                      </>
                    )}
                  </td>
                  <td className="small">{order.reference ?? '—'}</td>
                  <td className="small">{order.customer_name ?? '—'}</td>
                  <td className="small">{order.owner_name}</td>
                  <td className="small">{formatDate(order.requested_at)}</td>
                  <td>
                    {order.priority <= 3 ? (
                      <Badge kind="bad">{order.priority === 1 ? 'Express' : 'hoch'}</Badge>
                    ) : (
                      <span className="small muted">normal</span>
                    )}
                  </td>
                  <td>
                    <Badge kind={STATUS_STYLE[order.status]}>{STATUS_LABEL[order.status]}</Badge>
                  </td>
                  <td className="num small">
                    {formatQty(order.qty_picked)} / {formatQty(order.qty_ordered)}
                  </td>
                  <td className="num">{order.open_tasks || '—'}</td>
                  <td className="num">
                    <button className="small ghost" onClick={() => setOpenId(order.id)}>
                      öffnen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {openId && (
        <Card
          title={detail ? `Auftrag ${detail.order.number}` : 'Auftrag'}
          actions={
            <>
              {detail && mayBook && ['offen', 'kommissionierung'].includes(detail.order.status) && (
                <button className="small" onClick={makePicklist} disabled={busy}>
                  Pickliste erzeugen
                </button>
              )}
              {detail && mayBook && detail.order.qty_picked > detail.order.qty_shipped && (
                <button className="primary small" onClick={ship} disabled={busy}>
                  Versenden
                </button>
              )}
              {detail && mayBook && detail.order.status === 'offen' && (
                <button className="small" onClick={cancel} disabled={busy}>
                  stornieren
                </button>
              )}
              <button className="small" onClick={() => setOpenId(null)}>
                schliessen
              </button>
            </>
          }
          padded={false}
        >
          {!detail ? (
            <Loading />
          ) : (
            <>
              {detail.order.kind === 'angebot' && (
                <div className="body" style={{ borderBottom: '1px solid var(--line)' }}>
                  <Notice kind="info">
                    Ein Angebot reserviert keine Ware und erzeugt keine Pickliste. Beim Annehmen
                    entsteht daraus ein Auftrag; das Angebot bleibt als Geschäftsbrief erhalten.
                    {detail.order.valid_until
                      ? ` Bindefrist bis ${formatDate(detail.order.valid_until)}.`
                      : ''}
                  </Notice>
                  <button className="primary small" onClick={() => angebotAnnehmen()} disabled={busy}>
                    Angebot annehmen
                  </button>
                </div>
              )}
              <div className="body" style={{ borderBottom: '1px solid var(--line)' }}>
                <div className="small muted">
                  {detail.order.owner_name} · Lager {detail.order.warehouse_code}
                  {detail.order.customer_name ? ` · Kunde ${detail.order.customer_name}` : ''}
                  {detail.order.requested_at ? ` · Termin ${formatDate(detail.order.requested_at)}` : ''}
                  {detail.order.note ? ` · ${detail.order.note}` : ''}
                </div>
                {detail.order.fond_bezeichnung && (
                  <div className="small">
                    <strong>Fond:</strong>{' '}
                    {detail.order.fond_kennung
                      ? `${detail.order.fond_kennung} — ${detail.order.fond_bezeichnung}`
                      : detail.order.fond_bezeichnung}
                  </div>
                )}
              </div>

              <div className="tabs body" style={{ borderBottom: '1px solid var(--line)', paddingBottom: 0 }}>
                {(
                  [
                    ['positionen', 'Positionen'],
                    ['kommi', 'Kommissionierung'],
                    ['beleg', 'Beleg'],
                    ['sendung', 'Sendung'],
                  ] as ['positionen' | 'kommi' | 'beleg' | 'sendung', string][]
                ).map(([wert, label]) => (
                  <button
                    key={wert}
                    className={detailTab === wert ? 'active' : ''}
                    onClick={() => setDetailTab(wert)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {detailTab === 'positionen' && (
                <table>
                  <thead>
                    <tr>
                      <th>Pos</th>
                      <th>Artikel</th>
                      <th className="num">Bestellt</th>
                      <th className="num">Gepickt</th>
                      <th className="num">Versendet</th>
                      <th className="num">Einzelpreis</th>
                      <th className="num">Betrag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.lines.map((line) => (
                      <tr key={line.id}>
                        <td>{line.position}</td>
                        <td>
                          <span className="mono">{line.sku}</span> {line.article_name}
                          {line.discount_percent > 0 && (
                            <span className="small muted"> · {line.discount_percent} % Rabatt</span>
                          )}
                        </td>
                        <td className="num">{formatQty(line.qty_ordered, line.unit)}</td>
                        <td
                          className="num"
                          style={
                            line.qty_picked < line.qty_ordered ? { color: 'var(--warn)' } : undefined
                          }
                        >
                          {formatQty(line.qty_picked)}
                        </td>
                        <td className="num">{formatQty(line.qty_shipped)}</td>
                        <td className="num">
                          {line.unit_price_cents === null ? (
                            <Badge kind="warn">kein Preis</Badge>
                          ) : (
                            formatMoney(line.unit_price_cents)
                          )}
                        </td>
                        <td className="num">{formatMoney(line.amount_cents)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'right', fontWeight: 600 }}>
                        Netto
                      </td>
                      <td className="num" style={{ fontWeight: 600 }}>
                        {formatMoney(
                          detail.lines.reduce((summe, line) => summe + line.amount_cents, 0),
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}

              {detailTab === 'kommi' && (
                <>
                  {gefahrgut && gefahrgut.betroffen > 0 && (
                    <div className="body" style={{ paddingBottom: 0 }}>
                      <Notice kind={gefahrgut.freigestellt ? 'info' : 'error'}>
                        <strong>Gefahrgut nach ADR — {gefahrgut.meldung}</strong>
                        <div className="small" style={{ marginTop: 8 }}>
                          {gefahrgut.zeilen.map((zeile) => (
                            <div key={zeile.sku} style={{ marginBottom: 4 }}>
                              {zeile.angabe ? (
                                <span className="mono">{zeile.angabe}</span>
                              ) : (
                                <span>
                                  <span className="mono">{zeile.sku}</span> — Angaben unvollständig
                                </span>
                              )}
                              {zeile.menge !== null && (
                                <span className="muted"> · {formatQty(zeile.menge)} kg/l</span>
                              )}
                              {zeile.punkte !== null && (
                                <span className="muted"> · {Math.round(zeile.punkte)} Punkte</span>
                              )}
                              {zeile.begrenzte_menge && (
                                <span className="muted"> · begrenzte Menge (Kap. 3.4)</span>
                              )}
                            </div>
                          ))}
                        </div>
                        {gefahrgut.warnungen.length > 0 && (
                          <ul className="small" style={{ margin: '8px 0 0 16px' }}>
                            {gefahrgut.warnungen.map((warnung) => (
                              <li key={warnung}>{warnung}</li>
                            ))}
                          </ul>
                        )}
                        <div className="small muted" style={{ marginTop: 8 }}>
                          Die Punkterechnung nach 1.1.3.6 ist eine Hilfe, keine Freigabe:
                          Zusammenladeverbote und Tunnelbeschränkungen der Strecke stehen in keinem
                          Artikelstamm.
                        </div>
                      </Notice>
                    </div>
                  )}
                  {detail.tasks.length === 0 ? (
                    <div className="body">
                      <Empty>
                        Noch keine Pickliste. Über <strong>Pickliste erzeugen</strong> entsteht der
                        Arbeitsvorrat.
                      </Empty>
                    </div>
                  ) : (
                    <>
                      <table>
                        <thead>
                          <tr>
                            <th>Nr</th>
                            <th>Platz</th>
                            <th>Artikel</th>
                            <th>Charge</th>
                            <th className="num">Zu picken</th>
                            <th className="num">Gepickt</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.tasks.map((task) => (
                            <tr key={task.id}>
                              <td>{task.sequence}</td>
                              <td className="mono">{task.location_code}</td>
                              <td>
                                <span className="mono">{task.sku}</span> {task.article_name}
                              </td>
                              <td className="small">
                                {task.lot_code ?? '—'}
                                {task.best_before ? ` · ${formatDate(task.best_before)}` : ''}
                              </td>
                              <td className="num">{formatQty(task.qty_planned, task.unit)}</td>
                              <td className="num">{formatQty(task.qty_picked)}</td>
                              <td>
                                {task.status === 'offen' ? (
                                  <Badge kind="warn">offen</Badge>
                                ) : (
                                  <Badge kind="ok">erledigt</Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="body small muted">
                        Abgearbeitet wird die Liste unter <strong>Kommissionierung</strong> — dort
                        steht der Arbeitsvorrat aller Aufträge nach Lagerplatz sortiert.
                      </div>
                    </>
                  )}
                </>
              )}

              {detailTab === 'beleg' && (
                <div className="body">
                  <p className="small muted" style={{ marginTop: 0 }}>
                    Der Lieferschein als PDF — mit Positionen, Chargen und, sofern betroffen, den
                    Gefahrgutangaben nach ADR 5.4.1.1. Barcode der Lieferscheinnummer für den
                    Rücklauf.
                  </p>
                  {detail.order.qty_picked > 0 ? (
                    <button className="primary" onClick={ladeLieferschein}>
                      Lieferschein herunterladen
                    </button>
                  ) : (
                    <Empty>
                      Der Lieferschein steht bereit, sobald etwas kommissioniert wurde — er führt die
                      tatsächlich gepickten Chargen auf.
                    </Empty>
                  )}
                </div>
              )}

              {detailTab === 'sendung' && (
                <div className="body">
                  {detail.order.tracking_ref ? (
                    <>
                      <PZeile label="Versender">
                        {detail.order.carrier ? versenderName(detail.order.carrier) : '—'}
                      </PZeile>
                      <PZeile label="Sendungsnummer">
                        {verfolgungsLink(detail.order.carrier, detail.order.tracking_ref) ? (
                          <a
                            href={verfolgungsLink(detail.order.carrier, detail.order.tracking_ref)!}
                            target="_blank"
                            rel="noreferrer noopener"
                          >
                            {detail.order.tracking_ref}
                          </a>
                        ) : (
                          <span className="mono">{detail.order.tracking_ref}</span>
                        )}
                      </PZeile>
                      <PZeile label="Versendet am">{formatDate(detail.order.shipped_at)}</PZeile>
                      <PZeile label="Packstücke">
                        {detail.order.packages != null ? String(detail.order.packages) : '—'}
                      </PZeile>
                      <PZeile label="Gewicht">
                        {detail.order.weight_kg != null ? `${detail.order.weight_kg} kg` : '—'}
                      </PZeile>
                    </>
                  ) : (
                    <Empty>Noch nicht versendet — hier erscheint die Sendungsverfolgung.</Empty>
                  )}
                </div>
              )}
            </>
          )}
        </Card>
      )}
    </>
  );
}

function PZeile({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 10, padding: '3px 0' }}>
      <div className="small muted" style={{ minWidth: 140 }}>
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}
